import { ImageUrlInput } from '@/components/media/ImageUrlInput';
import { MediaPickerButton } from '@/components/media/MediaPickerButton';
import { useDeviceType } from '@/hooks/use-device-type';
import { type ProductFormValues, type ProductUpdateValues } from '@/types/product-type';
import { Grid } from '@mui/material';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { MySection } from '../../custom/my-section';

interface ProductMediaProps {
  control: Control<ProductFormValues | ProductUpdateValues>;
  register: UseFormRegister<ProductFormValues | ProductUpdateValues>;
  errors: FieldErrors<ProductFormValues | ProductUpdateValues>;
  setValue: UseFormSetValue<ProductFormValues | ProductUpdateValues>;
  watch: UseFormWatch<ProductFormValues | ProductUpdateValues>;
  inline?: boolean;
}

export function ProductMediaFormSection({ control, errors, setValue, watch, inline }: ProductMediaProps) {
  const imageUrlValue = watch('imageUrl' as any) as string | undefined;
  const { isMobile } = useDeviceType();

  const setImageUrl = (url: string) =>
    setValue('imageUrl' as any, url, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

  const content = (
    <Grid container gap={2}>
      <Grid item xs={12}>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-slate-700">Image</label>

          {/*
            Mobile:  ImageUrlInput stacks full-width (shows tap-to-upload zone),
                     MediaPickerButton appears below as a secondary "Browse" action.
            Desktop: Side-by-side row — input + picker button.
          */}
          <div className={isMobile ? 'flex flex-col gap-2' : 'flex gap-2 items-start'}>
            <Controller
              control={control}
              name={'imageUrl' as any}
              render={({ field }) => (
                <ImageUrlInput
                  value={field.value ?? ''}
                  onChange={setImageUrl}
                  placeholder="https://example.com/image.jpg or drag & drop an image"
                  className="flex-1"
                />
              )}
            />

            {/* Media browser picker */}
            <div className={isMobile ? 'self-start' : 'mt-0 shrink-0'}>
              <MediaPickerButton
                label={isMobile ? 'Browse media library' : 'Choose image'}
                multi={false}
                allowUpload
                previewUrl={isMobile ? undefined : (imageUrlValue || undefined)}
                onClear={() => setImageUrl('')}
                onPick={(items) => {
                  const url = items[0]?.s3Url || items[0]?.fileUrl || '';
                  setImageUrl(url);
                }}
              />
            </div>
          </div>

          {(errors as any).imageUrl && (
            <span className="text-xs text-red-500">
              {(errors as any).imageUrl.message as string}
            </span>
          )}
        </div>
      </Grid>
    </Grid>
  );

  return inline ? content : (
    <MySection title="Media Management">
      {content}
    </MySection>
  );
}
